/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import * as moment from 'moment';
import { Just, Maybe, Nothing } from 'purify-ts';
import { EitherAsync } from 'purify-ts/EitherAsync';
import { getStoreBuilder } from 'vuex-typex';
import * as api from '@codegrade/api';
import * as siteSettings from '@codegrade/site-settings';
import { eitherAsync, object } from '@codegrade/utils';
import client, { unauthorizedClient } from '@/client';
import * as utils from '@/utils';
import { UserStore } from './user';
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => ({
    siteSettings: Maybe.empty(),
    siteLoader: Maybe.empty(),
    serverRelease: undefined,
    serverTimeOffset: Nothing,
    tenantSettings: {},
    tenantLoaders: {},
});
const moduleBuilder = storeBuilder.module('siteSettings', makeInitialState());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var SiteSettingsStore;
(function (SiteSettingsStore) {
    /**
     * Get a function that maybe retrieves a site setting, returning `Nothing` if it couldn't be
     * found.
     *
     * @param state The state to retrieve the settings from.
     * @returns A function that given a site setting and the tenant id retrieves a site settings,
     * returning `Nothing` if it couldn't be found.
     */
    function maybeGetSettingFunc(state) {
        return (opt, tenantId) => tenantId
            .mapOrDefault(t => Maybe.fromNullable(state.tenantSettings[t]), state.siteSettings)
            .chainNullable(object.attr(opt));
    }
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _getSetting(state) {
        const maybeGetter = maybeGetSettingFunc(state);
        return (opt, tenantId) => maybeGetter(opt, tenantId)
            .ifNothing(() => {
            utils.loadBackgroundApis(
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            SiteSettingsStore.loadSettings({ force: false, catchAllErrors: true, tenantId }));
        })
            .orDefaultLazy(() => siteSettings.FRONTEND_SETTINGS_DEFAULTS[opt]);
    }
    /**
     * A getter to maybe retrieve a site setting, this will return `Nothing` while the setting is
     * not loaded yet.
     *
     * This function will not load the setting if it is not loaded yet.
     */
    SiteSettingsStore.maybeGetSetting = moduleBuilder.read(maybeGetSettingFunc, 'maybeGetSetting');
    /**
     * A getter to retrieve a site setting.
     *
     * When the settings are not loaded yet we will default to retrieving the value and kick off an
     * action to load the settings.
     */
    SiteSettingsStore.getSetting = moduleBuilder.read(_getSetting, 'getSetting');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.releaseInfo = moduleBuilder.read(state => Maybe.fromNullable(state.serverRelease).filter(info => info.commit === COMMIT_HASH), 'releaseInfo');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.getServerTimeOffset = moduleBuilder.read(state => state.serverTimeOffset, 'getServerTimeOffset');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.currentTimeGetter = moduleBuilder.read(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    () => () => {
        const tenantId = UserStore.tenantId();
        const correction = SiteSettingsStore.getServerTimeOffset().filter(offset => {
            const shouldCorrect = SiteSettingsStore.getSetting()('SERVER_TIME_CORRECTION_ENABLED', tenantId);
            if (!shouldCorrect) {
                return false;
            }
            const diffTolerance = SiteSettingsStore.getSetting()('SERVER_TIME_DIFF_TOLERANCE', tenantId);
            return Math.abs(offset.asSeconds()) > diffTolerance.asSeconds();
        });
        return utils.datetime.momentUtc(correction);
    }, 'currentTimeGetter');
    const removeLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { tenantId }) => {
        tenantId.caseOf({
            Just: t => {
                utils.vueDelete(state.tenantLoaders, t);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteLoader', Maybe.empty());
            },
        });
    }, 'removeLoader');
    const commitLoader = moduleBuilder.commit((state, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    { tenantId, loader }) => {
        loader.ifLeft(() => removeLoader({ tenantId }));
        tenantId.caseOf({
            Just: t => {
                utils.vueSet(state.tenantLoaders, t, loader);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteLoader', Maybe.of(loader));
            },
        });
    }, 'commitLoader');
    const commitServerRelease = moduleBuilder.commit((state, { version, date, message, uiPreference, commit }) => {
        if (version == null || date == null || message == null || uiPreference == null) {
            state.serverRelease = {
                commit,
            };
        }
        else {
            state.serverRelease = {
                version,
                message,
                commit,
                date,
                uiPreference,
            };
        }
    }, 'commitServerCommit');
    const commitServerTime = moduleBuilder.commit((state, data) => {
        state.serverTimeOffset = Just(moment.duration(data.diff(utils.datetime.momentUtc(Maybe.empty()))));
    }, 'commitServerTime');
    const clearTenantSettings = moduleBuilder.commit(state => {
        state.tenantSettings = {};
        state.tenantLoaders = {};
    }, 'clearTenantSettings');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.commitSettings = moduleBuilder.commit((state, { tenantId, settings, }) => {
        tenantId.caseOf({
            Just: t => {
                utils.vueSet(state.tenantSettings, t, settings);
            },
            Nothing: () => {
                utils.vueSet(state, 'siteSettings', Maybe.of(settings));
            },
        });
    }, 'commitSettings');
    const patchSettings = moduleBuilder.commit((state, { tenantId, settings, }) => {
        tenantId.caseOf({
            Just: t => {
                const oldSettings = state.tenantSettings[t] ?? {};
                utils.vueSet(state.tenantSettings, t, { ...oldSettings, ...settings });
            },
            Nothing: () => {
                state.siteSettings.ifJust(oldSettings => {
                    utils.vueSet(state, 'siteSettings', Maybe.of({ ...oldSettings, ...settings }));
                    clearTenantSettings();
                });
            },
        });
    }, 'patchSettings');
    /** Function to load the site settings into the store. */
    SiteSettingsStore.loadSiteSettings = moduleBuilder.dispatch(({ state }, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    payload = {}) => {
        const { force = false, forLoggedInUser = false, catchAllErrors = false } = payload;
        const tenantId = UserStore.loggedInUser()
            .filter(() => forLoggedInUser)
            .chain(u => u.tenantId);
        return utils.loadApiWithMaybeForce({
            force,
            value: state.siteSettings,
            loader: state.siteLoader,
            load: () => unauthorizedClient.about
                .get({ tenantId: tenantId.extract() })
                .ifRight(({ cgResult }) => {
                commitServerTime(cgResult.currentTime);
                commitServerRelease(cgResult.release);
                SiteSettingsStore.commitSettings({
                    settings: siteSettings.frontendWithDefaults(cgResult.settings),
                    tenantId,
                });
            })
                .chainLeft(err => {
                if (catchAllErrors) {
                    return utils.emptyResponse();
                }
                return eitherAsync.leftFromValue(err);
            }),
            addLoader: loader => {
                commitLoader({
                    tenantId: Maybe.empty(),
                    loader,
                });
                tenantId.ifJust(() => {
                    commitLoader({ tenantId, loader });
                });
            },
            removeLoader: () => {
                removeLoader({ tenantId: Maybe.empty() });
                tenantId.ifJust(() => {
                    removeLoader({ tenantId });
                });
            },
        });
    }, 'loadSiteSettings');
    /** Function to load the tenant site settings into the store. */
    SiteSettingsStore.loadTenantSettings = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, payload) => {
        const { tenantId, force = false, catchAllErrors = false } = payload;
        return utils.loadApiWithMaybeForce({
            force,
            value: Maybe.fromNullable(state.tenantSettings[tenantId]),
            loader: Maybe.fromNullable(state.tenantLoaders[tenantId]),
            load: () => unauthorizedClient.tenant
                .getSettings({
                tenantId,
                onlyFrontend: true,
            })
                .chain(response => EitherAsync.liftEither(response
                .mapEither(cgResult => {
                // This route will always return a FrontendSiteSettings even
                // though its return type is a AllSiteSettings, so we
                // encode and decode it again but now with the correct type.
                const encoded = api.AllSiteSettings.encode(cgResult);
                return api.FrontendSiteSettings.decode(encoded).mapLeft(Error);
            })
                .ifRight(({ cgResult }) => {
                SiteSettingsStore.commitSettings({
                    tenantId: Maybe.of(tenantId),
                    settings: siteSettings.frontendWithDefaults(cgResult),
                });
            })))
                .chainLeft(err => {
                if (catchAllErrors) {
                    return utils.emptyResponse();
                }
                return eitherAsync.leftFromValue(err);
            }),
            addLoader: loader => commitLoader({
                tenantId: Maybe.of(tenantId),
                loader,
            }),
            removeLoader: () => removeLoader({ tenantId: Maybe.of(tenantId) }),
        });
    }, 'loadTenantSettings');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.loadSettings = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => {
        const { tenantId, force, catchAllErrors } = payload;
        return tenantId.caseOf({
            Just: t => SiteSettingsStore.loadTenantSettings({ tenantId: t, force, catchAllErrors }),
            Nothing: () => SiteSettingsStore.loadSiteSettings({ force, catchAllErrors }),
        });
    }, 'loadSettings');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SiteSettingsStore.updateSettings = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, payload) => {
        const { tenantId, data } = payload;
        const requestBody = { updates: data };
        /**
         * This function updates tenant site settings in the backend.
         *
         * @param tId The tenant id to patch settings for.
         * @returns An api result containing the updated settings or an error.
         */
        const patchTenantSettings = (tId) => client.tenant.patchSettings({ tenantId: tId, requestBody });
        return tenantId
            .map(patchTenantSettings)
            .orDefaultLazy(() => {
            // Invalidate all tenant settings because they fall back
            // to the global site settings which have just changed.
            clearTenantSettings();
            return client.siteSettings.patch({ requestBody });
        })
            .ifRight(({ cgResult }) => patchSettings({ tenantId, settings: cgResult }));
    }, 'updateSettings');
})(SiteSettingsStore || (SiteSettingsStore = {}));
